import { getSupabase } from '@/lib/supabase';
import type { ClientTracker, TrackerEntry, TrackerUnit } from '@/types/trackers';
import { throwIfNotOk } from '@/lib/apiClient';
import * as trackersApi from '@/data/trackersApi';

// Map database row to ClientTracker interface
function mapTrackerFromDb(row: any): ClientTracker {
  return {
    id: row.id,
    clientId: row.client_id,
    ownerCoachId: row.owner_coach_id,
    title: row.title,
    description: row.description,
    unit: row.unit as TrackerUnit,
    targetDirection: row.target_direction,
    schedule: row.schedule,
    showInAnalytics: row.show_in_analytics,
    isArchived: row.is_archived,
    lastLoggedAtISO: row.last_logged_at, // Map to correct property name
    nextDueOn: row.next_due_on,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// Map ClientTracker to database row
function mapTrackerToDb(tracker: Partial<ClientTracker>): any {
  return {
    id: tracker.id,
    client_id: tracker.clientId,
    owner_coach_id: tracker.ownerCoachId,
    title: tracker.title,
    description: tracker.description,
    unit: tracker.unit,
    target_direction: tracker.targetDirection,
    schedule: tracker.schedule,
    show_in_analytics: tracker.showInAnalytics,
    is_archived: tracker.isArchived,
    last_logged_at: tracker.lastLoggedAtISO, // Map from correct property name
    next_due_on: tracker.nextDueOn,
  };
}

export async function listTrackers(clientId: string): Promise<ClientTracker[]> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const { data, error } = await sb
    .from('client_trackers')
    .select('*')
    .eq('client_id', clientId)
    .eq('is_archived', false)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []).map(mapTrackerFromDb);
}

export async function createTracker(
  tracker: Omit<ClientTracker, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<ClientTracker> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const dbTracker = mapTrackerToDb(tracker);
  delete dbTracker.id; // Let database generate UUID

  const { data, error } = await sb.from('client_trackers').insert(dbTracker).select().single();

  if (error) throw error;
  return mapTrackerFromDb(data);
}

export async function updateTracker(trackerId: string, updates: Partial<ClientTracker>): Promise<ClientTracker> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const dbUpdates = mapTrackerToDb(updates);
  delete dbUpdates.id; // Don't update the ID

  const { data, error } = await sb.from('client_trackers').update(dbUpdates).eq('id', trackerId).select().single();

  if (error) throw error;
  return mapTrackerFromDb(data);
}

export async function deleteTracker(trackerId: string): Promise<void> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const { error } = await sb.from('client_trackers').delete().eq('id', trackerId);

  if (error) throw error;
}

export async function listTrackerEntries(trackerId: string): Promise<TrackerEntry[]> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const { data, error } = await sb
    .from('client_tracker_entries')
    .select('*')
    .eq('tracker_id', trackerId)
    .order('logged_at', { ascending: false });

  if (error) throw error;
  return (data || []).map((row) => ({
    id: row.id,
    trackerId: row.tracker_id,
    clientId: row.client_id,
    // TODO(sec2-followup-5): values are encrypted at rest (row.value is always null), so this is NaN on
    // every entry. Needs a client GET endpoint that decrypts, like GET /api/coach/tracker-entries.
    value: parseFloat(row.value),
    note: row.note,
    at: row.logged_at, // Map to 'at' property
    createdBy: row.created_by,
  }));
}

export async function getCoachTrackersForClient(clientId: string, coachId: string): Promise<ClientTracker[]> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const { data, error } = await sb
    .from('client_trackers')
    .select('*')
    .eq('client_id', clientId)
    .eq('owner_coach_id', coachId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []).map(mapTrackerFromDb);
}

export async function getCoachActivityFeed(coachId: string, limit: number = 50): Promise<any[]> {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  // Get tracker entries for coach's clients
  const { data: entries, error: entriesError } = await sb
    .from('client_tracker_entries')
    .select(
      `
      id,
      tracker_id,
      client_id,
      value,
      note,
      logged_at,
      created_by,
      client_trackers!inner(
        id,
        title,
        unit,
        owner_coach_id
      )
    `,
    )
    .eq('client_trackers.owner_coach_id', coachId)
    .order('logged_at', { ascending: false })
    .limit(limit);

  if (entriesError) throw entriesError;

  // Get client names separately (simplified query)
  const clientIds = [...new Set((entries || []).map((e) => e.client_id))];
  const { data: clients, error: clientsError } = await sb
    .from('my_clients') // Corrected table name
    .select('id, full_name, email') // Corrected column names
    .in('id', clientIds);

  if (clientsError) throw clientsError;

  // Create client name lookup
  const clientNameMap = new Map();
  (clients || []).forEach((client) => {
    const clientName = client.full_name || client.email || 'Unknown Client'; // Use full_name or email
    clientNameMap.set(client.id, clientName);
  });

  // Map entries to activity format
  return (entries || []).map((entry: any) => ({
    id: entry.id,
    type: 'tracker_logged',
    summary: `${clientNameMap.get(entry.client_id)} logged ${entry.value} ${entry.client_trackers?.unit} for ${entry.client_trackers?.title}`,
    details: {
      trackerId: entry.tracker_id,
      trackerTitle: entry.client_trackers?.title,
      trackerUnit: entry.client_trackers?.unit,
      value: entry.value,
      note: entry.note,
      clientId: entry.client_id,
      clientName: clientNameMap.get(entry.client_id),
    },
    created_at: entry.logged_at,
    client_id: entry.client_id,
    coach_id: entry.client_trackers?.owner_coach_id,
  }));
}

// Export the trackers store interface for adapter compatibility
export const supabaseTrackersStore = {
  async listTrackers(clientId: string): Promise<ClientTracker[]> {
    return listTrackers(clientId);
  },
  async upsertTracker(tracker: ClientTracker): Promise<void> {
    // Check if tracker exists
    const existing = await listTrackers(tracker.clientId);
    const found = existing.find((t) => t.id === tracker.id);

    if (found) {
      await updateTracker(tracker.id, tracker);
    } else {
      await createTracker(tracker);
    }
  },
  async deleteTracker(trackerId: string): Promise<void> {
    const sb = getSupabase();
    if (!sb) throw new Error('Supabase not configured');

    const { error } = await sb.from('client_trackers').delete().eq('id', trackerId);

    if (error) throw error;
  },
  async listEntries(trackerId: string): Promise<TrackerEntry[]> {
    return listTrackerEntries(trackerId);
  },
  // Entry values are encrypted at rest with a server-only key, so entries are written through
  // the Worker endpoints, never inserted from the browser (RLS rejects a plaintext value).
  async addEntry(entry: TrackerEntry): Promise<void> {
    const res =
      entry.createdBy === 'coach'
        ? await trackersApi.addTrackerEntry({
            trackerId: entry.trackerId,
            clientId: entry.clientId,
            value: entry.value,
            note: entry.note,
            createdBy: 'coach',
          })
        : await trackersApi.logClientTrackerEntry({
            trackerId: entry.trackerId,
            value: entry.value,
            note: entry.note,
            loggedAt: entry.at,
          });
    throwIfNotOk(res);
  },
  async listEntriesForClient(clientId: string, coachId?: string): Promise<TrackerEntry[]> {
    const sb = getSupabase();
    if (!sb) throw new Error('Supabase not configured');

    // Two-step RLS-friendly query:
    // 1) Get trackers (filtered by coach ownership if coachId provided)
    let trackerQuery = sb.from('client_trackers').select('id').eq('client_id', clientId).eq('is_archived', false);

    if (coachId) {
      trackerQuery = trackerQuery.eq('owner_coach_id', coachId);
    }

    const { data: trackers, error: tErr } = await trackerQuery;

    if (tErr) {
      console.error('[RLS] Failed to fetch trackers:', tErr);
      return [];
    }

    const trackerIds = (trackers || []).map((t) => t.id);
    if (trackerIds.length === 0) return [];

    // 2) Get entries by tracker_id (RLS-friendly via parent tracker ownership)
    const { data, error: eErr } = await sb
      .from('client_tracker_entries')
      .select('*')
      .in('tracker_id', trackerIds)
      .order('logged_at', { ascending: false })
      .limit(200);

    if (eErr) {
      console.error('[RLS] Failed to fetch tracker entries:', eErr);
      return [];
    }

    return (data || []).map((row) => ({
      id: row.id,
      trackerId: row.tracker_id,
      clientId: row.client_id,
      // TODO(sec2-followup-5): values are encrypted at rest (row.value is always null), so this is NaN on
      // every entry. Needs a client GET endpoint that decrypts, like GET /api/coach/tracker-entries.
      value: parseFloat(row.value),
      note: row.note,
      at: row.logged_at,
      createdBy: row.created_by,
    }));
  },
  // Additional methods for GlobalStateProvider compatibility
  createTracker,
  updateTracker,
  listTrackerEntries,
  getCoachTrackersForClient,
  getCoachActivityFeed,
};
